/**
 * Enhanced Cost Calculator Component
 * Professional cost calculator with charts, visualizations, and side-by-side comparisons
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  Calculator,
  TrendingUp,
  DollarSign,
  Clock,
  BarChart3,
  PieChart,
  ArrowRight,
  Zap,
  Shield,
  Globe
} from 'lucide-react'
import { mockApiService } from '../services/mockApiService'
import { MockExchangeRate } from '../services/mockApi'

interface CostCalculation {
  amount: number
  stablecoinAmount: number
  exchangeRate: number
  fxCost: number
  processingFee: number
  totalCost: number
  traditionalCost: number
  savings: number
  savingsPercentage: number
  settlementRail: string
  estimatedTime: string
}

interface AnnualProjection {
  monthlyVolume: number
  annualVolume: number
  annualSavings: number
  traditionalAnnualCost: number
  stablePayAnnualCost: number
  breakEvenMonths: number
}

interface EnhancedCostCalculatorProps {
  className?: string
  mode?: 'business' | 'customer'
  invoiceAmount?: number
  invoiceCurrency?: string
}

export const EnhancedCostCalculator: React.FC<EnhancedCostCalculatorProps> = ({ 
  className = '', 
  mode = 'business',
  invoiceAmount,
  invoiceCurrency = 'HKD'
}) => {
  // Form state
  const [amount, setAmount] = useState<string>(invoiceAmount?.toString() || '10000')
  const [currency, setCurrency] = useState<string>(invoiceCurrency)
  const [stablecoin, setStablecoin] = useState<string>('USDT')
  const [settlementRail, setSettlementRail] = useState<string>('FPS')
  const [monthlyVolume, setMonthlyVolume] = useState<string>(mode === 'customer' ? '25000' : '50000')

  // Data state
  const [calculation, setCalculation] = useState<CostCalculation | null>(null)
  const [exchangeRates, setExchangeRates] = useState<MockExchangeRate[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  // UI state
  const [activeTab, setActiveTab] = useState<'single' | 'annual'>('single')
  const [chartType, setChartType] = useState<'bar' | 'pie'>('bar')

  // Load exchange rates on mount
  useEffect(() => {
    loadExchangeRates()
  }, [])

  // Calculate costs when inputs change
  useEffect(() => {
    if (amount && !isNaN(parseFloat(amount))) {
      calculateCosts()
    }
  }, [amount, stablecoin, settlementRail])

  const loadExchangeRates = async () => {
    try {
      const response = await mockApiService.getExchangeRates()
      if (response.success) {
        setExchangeRates(response.data)
      }
    } catch (err) {
      console.error('Failed to load exchange rates:', err)
    }
  }

  const calculateCosts = async () => {
    if (!amount || isNaN(parseFloat(amount))) return

    setLoading(true)
    setError('')

    try {
      let response
      if (mode === 'customer') {
        response = await mockApiService.calculateCustomerPaymentCost(
          parseFloat(amount),
          stablecoin
        )
        
        if (response.success) {
          // Transform customer response to match business calculation format
          const customerData = response.data
          setCalculation({
            amount: customerData.invoiceAmount,
            stablecoinAmount: customerData.stablecoinAmount,
            exchangeRate: customerData.exchangeRate,
            fxCost: customerData.breakdown.fxCost,
            processingFee: customerData.breakdown.processingFee,
            totalCost: customerData.breakdown.totalFees,
            traditionalCost: customerData.comparison.traditionalFees,
            savings: customerData.comparison.savings,
            savingsPercentage: customerData.comparison.savingsPercentage,
            settlementRail: settlementRail,
            estimatedTime: customerData.estimatedConfirmationTime
          })
        }
      } else {
        response = await mockApiService.calculateCosts(
          parseFloat(amount),
          stablecoin,
          settlementRail
        )
        
        if (response.success) {
          setCalculation(response.data)
        }
      }

      if (!response.success) {
        setError('Failed to calculate costs')
      }
    } catch (err) {
      setError('Error calculating costs')
      console.error('Cost calculation error:', err)
    } finally {
      setLoading(false)
    }
  }

  // Calculate annual projections
  const annualProjection = useMemo((): AnnualProjection | null => {
    if (!calculation || !monthlyVolume || isNaN(parseFloat(monthlyVolume))) {
      return null
    }

    const monthly = parseFloat(monthlyVolume)
    const annual = monthly * 12
    const savingsPerTransaction = calculation.savings
    const savingsRate = calculation.savingsPercentage / 100

    const annualSavings = (annual / parseFloat(amount)) * savingsPerTransaction
    const traditionalAnnualCost = annual * 0.035 // 3.5% traditional cost
    const stablePayAnnualCost = traditionalAnnualCost - annualSavings

    return {
      monthlyVolume: monthly,
      annualVolume: annual,
      annualSavings,
      traditionalAnnualCost,
      stablePayAnnualCost,
      breakEvenMonths: annualSavings > 0 ? Math.ceil(12 / (annualSavings / (traditionalAnnualCost / 12))) : 0
    }
  }, [calculation, monthlyVolume, amount])

  // Chart data for cost breakdown
  const costBreakdownData = useMemo(() => {
    if (!calculation) return null

    return [
      { name: 'FX Cost', value: calculation.fxCost, color: '#3B82F6' },
      { name: 'Processing Fee', value: calculation.processingFee, color: '#10B981' },
      { name: 'Savings vs Traditional', value: calculation.savings, color: '#F59E0B' }
    ]
  }, [calculation])

  // Settlement rail options
  const settlementRails = [
    { value: 'FPS', label: 'FPS (HKD)', time: '15 minutes', fee: '0.1%' },
    { value: 'CIPS', label: 'CIPS (CNY)', time: 'Same day', fee: '0.2%' },
    { value: 'e-CNY', label: 'e-CNY (Digital Yuan)', time: 'Instant', fee: '0.05%' }
  ]

  const stablecoinOptions = [
    { value: 'USDT', label: 'USDT (Tether)', icon: '₮' },
    { value: 'USDC', label: 'USDC (USD Coin)', icon: '$' },
    { value: 'HKD₮', label: 'HKD₮ (HK Dollar)', icon: 'H$' },
    { value: 'CNH₮', label: 'CNH₮ (Offshore RMB)', icon: '¥' }
  ]

  return (
    <div className={`enhanced-cost-calculator ${className}`}>
      {/* Header */}
      <div className="enhanced-cost-calculator__header">
        <div className="flex items-center gap-3">
          <div className="enhanced-cost-calculator__icon">
            <Calculator className="w-6 h-6" />
          </div>
          <div>
            <h2 className="enhanced-cost-calculator__title">
              {mode === 'customer' ? 'Payment Cost Calculator' : 'Cost Calculator'}
            </h2>
            <p className="enhanced-cost-calculator__subtitle">
              {mode === 'customer' 
                ? 'See how much you save vs traditional international transfers'
                : 'Compare StablePay costs with traditional payment methods'
              }
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="enhanced-cost-calculator__tabs">
          <button
            className={`enhanced-cost-calculator__tab ${activeTab === 'single' ? 'enhanced-cost-calculator__tab--active' : ''}`}
            onClick={() => setActiveTab('single')}
          >
            <DollarSign className="w-4 h-4" />
            {mode === 'customer' ? 'This Payment' : 'Single Payment'}
          </button>
          <button
            className={`enhanced-cost-calculator__tab ${activeTab === 'annual' ? 'enhanced-cost-calculator__tab--active' : ''}`}
            onClick={() => setActiveTab('annual')}
          >
            <TrendingUp className="w-4 h-4" />
            {mode === 'customer' ? 'Annual Savings' : 'Annual Projection'}
          </button>
        </div>
      </div>

      {/* Input Controls */}
      <div className="enhanced-cost-calculator__controls">
        <div className="enhanced-cost-calculator__control-group">
          <label htmlFor="amount-input" className="enhanced-cost-calculator__label">
            {mode === 'customer' ? `Invoice Amount (${currency})` : `Payment Amount (${currency})`}
          </label>
          <input
            id="amount-input"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="enhanced-cost-calculator__input"
            placeholder={mode === 'customer' ? 'Invoice amount' : 'Enter amount'}
            min="1"
            step="0.01"
            disabled={mode === 'customer' && invoiceAmount !== undefined}
          />
        </div>

        <div className="enhanced-cost-calculator__control-group">
          <label htmlFor="stablecoin-select" className="enhanced-cost-calculator__label">
            Stablecoin Type
          </label>
          <select
            id="stablecoin-select"
            value={stablecoin}
            onChange={(e) => setStablecoin(e.target.value)}
            className="enhanced-cost-calculator__select"
          >
            {stablecoinOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="enhanced-cost-calculator__control-group">
          <label htmlFor="settlement-rail-select" className="enhanced-cost-calculator__label">
            Settlement Rail
          </label>
          <select
            id="settlement-rail-select"
            value={settlementRail}
            onChange={(e) => setSettlementRail(e.target.value)}
            className="enhanced-cost-calculator__select"
          >
            {settlementRails.map(rail => (
              <option key={rail.value} value={rail.value}>
                {rail.label} - {rail.time}
              </option>
            ))}
          </select>
        </div>

        {activeTab === 'annual' && (
          <div className="enhanced-cost-calculator__control-group">
            <label htmlFor="monthly-volume-input" className="enhanced-cost-calculator__label">
              Monthly Volume ({currency})
            </label>
            <input
              id="monthly-volume-input"
              type="number"
              value={monthlyVolume}
              onChange={(e) => setMonthlyVolume(e.target.value)}
              className="enhanced-cost-calculator__input"
              placeholder="Enter monthly volume"
              min="1"
              step="1000"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="enhanced-cost-calculator__error">
          {error}
        </div>
      )}

      {loading && (
        <div className="enhanced-cost-calculator__loading">
          <div className="enhanced-cost-calculator__spinner"></div>
          Calculating costs...
        </div>
      )}

      {/* Results Section */}
      {calculation && !loading && (
        <>
          {activeTab === 'single' && (
            <SinglePaymentResults 
              calculation={calculation}
              costBreakdownData={costBreakdownData}
              chartType={chartType}
              onChartTypeChange={setChartType}
              mode={mode}
            />
          )}

          {activeTab === 'annual' && annualProjection && (
            <AnnualProjectionResults 
              calculation={calculation}
              projection={annualProjection}
              mode={mode}
            />
          )}
        </>
      )}
    </div>
  )
}

// Single Payment Results Component
interface SinglePaymentResultsProps {
  calculation: CostCalculation
  costBreakdownData: any[] | null
  chartType: 'bar' | 'pie'
  onChartTypeChange: (type: 'bar' | 'pie') => void
  mode?: 'business' | 'customer'
}

const SinglePaymentResults: React.FC<SinglePaymentResultsProps> = ({
  calculation,
  costBreakdownData,
  chartType,
  onChartTypeChange,
  mode = 'business'
}) => {
  return (
    <div className="enhanced-cost-calculator__results">
      {/* Side-by-side Comparison */}
      <div className="enhanced-cost-calculator__comparison">
        {/* Traditional Method */}
        <div className="enhanced-cost-calculator__comparison-card enhanced-cost-calculator__comparison-card--traditional">
          <div className="enhanced-cost-calculator__comparison-header">
            <Globe className="w-5 h-5" />
            <h3>{mode === 'customer' ? 'Traditional Bank Transfer' : 'Traditional SWIFT'}</h3>
          </div>
          <div className="enhanced-cost-calculator__comparison-content">
            <div className="enhanced-cost-calculator__cost-item">
              <span>{mode === 'customer' ? 'Your Cost:' : 'Total Cost:'}</span>
              <span className="enhanced-cost-calculator__cost-value enhanced-cost-calculator__cost-value--high">
                {calculation.traditionalCost.toFixed(2)} HKD
              </span>
            </div>
            <div className="enhanced-cost-calculator__cost-item">
              <span>{mode === 'customer' ? 'Transfer Time:' : 'Processing Time:'}</span>
              <span>3-5 business days</span>
            </div>
            <div className="enhanced-cost-calculator__cost-item">
              <span>{mode === 'customer' ? 'Hidden Fees:' : 'Fee Structure:'}</span>
              <span>{mode === 'customer' ? 'Up to 3.5% + fixed fees' : '~3.5% total cost'}</span>
            </div>
          </div>
        </div>

        {/* Arrow */}
        <div className="enhanced-cost-calculator__comparison-arrow">
          <ArrowRight className="w-6 h-6" />
        </div>

        {/* StablePay Method */}
        <div className="enhanced-cost-calculator__comparison-card enhanced-cost-calculator__comparison-card--stablepay">
          <div className="enhanced-cost-calculator__comparison-header">
            <Zap className="w-5 h-5" />
            <h3>{mode === 'customer' ? 'Stablecoin Payment' : 'StablePay'}</h3>
          </div>
          <div className="enhanced-cost-calculator__comparison-content">
            <div className="enhanced-cost-calculator__cost-item">
              <span>{mode === 'customer' ? 'Your Cost:' : 'Total Cost:'}</span>
              <span className="enhanced-cost-calculator__cost-value enhanced-cost-calculator__cost-value--low">
                {calculation.totalCost.toFixed(2)} HKD
              </span>
            </div>
            <div className="enhanced-cost-calculator__cost-item">
              <span>{mode === 'customer' ? 'Payment Time:' : 'Processing Time:'}</span>
              <span>{mode === 'customer' ? '2-5 minutes' : calculation.estimatedTime}</span>
            </div>
            <div className="enhanced-cost-calculator__cost-item">
              <span>{mode === 'customer' ? 'You Save:' : 'You Save:'}</span>
              <span className="enhanced-cost-calculator__savings">
                {calculation.savings.toFixed(2)} HKD ({calculation.savingsPercentage.toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Speed Comparison for Customer Mode */}
      {mode === 'customer' && (
        <div className="enhanced-cost-calculator__speed-comparison">
          <h3>Speed Advantage</h3>
          <div className="enhanced-cost-calculator__speed-bars">
            <div className="enhanced-cost-calculator__speed-item">
              <div className="enhanced-cost-calculator__speed-label">
                <Globe className="w-4 h-4" />
                Traditional Bank Transfer
              </div>
              <div className="enhanced-cost-calculator__speed-bar">
                <div className="enhanced-cost-calculator__speed-fill enhanced-cost-calculator__speed-fill--slow"></div>
                <span className="enhanced-cost-calculator__speed-time">3-5 business days</span>
              </div>
            </div>
            <div className="enhanced-cost-calculator__speed-item">
              <div className="enhanced-cost-calculator__speed-label">
                <Zap className="w-4 h-4" />
                Stablecoin Payment
              </div>
              <div className="enhanced-cost-calculator__speed-bar">
                <div className="enhanced-cost-calculator__speed-fill enhanced-cost-calculator__speed-fill--fast"></div>
                <span className="enhanced-cost-calculator__speed-time">2-5 minutes</span>
              </div>
            </div>
          </div>
          <div className="enhanced-cost-calculator__speed-highlight">
            <Clock className="w-5 h-5 text-green-600" />
            <span>99% faster than traditional international transfers</span>
          </div>
        </div>
      )}

      {/* Detailed Breakdown */}
      <div className="enhanced-cost-calculator__breakdown">
        <div className="enhanced-cost-calculator__breakdown-header">
          <h3>Cost Breakdown</h3>
          <div className="enhanced-cost-calculator__chart-controls">
            <button
              className={`enhanced-cost-calculator__chart-btn ${chartType === 'bar' ? 'enhanced-cost-calculator__chart-btn--active' : ''}`}
              onClick={() => onChartTypeChange('bar')}
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            <button
              className={`enhanced-cost-calculator__chart-btn ${chartType === 'pie' ? 'enhanced-cost-calculator__chart-btn--active' : ''}`}
              onClick={() => onChartTypeChange('pie')}
            >
              <PieChart className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="enhanced-cost-calculator__breakdown-content">
          <div className="enhanced-cost-calculator__breakdown-details">
            <div className="enhanced-cost-calculator__breakdown-item">
              <span>Stablecoin Amount:</span>
              <span>{calculation.stablecoinAmount.toFixed(6)} {calculation.stablecoinAmount > 1 ? 'USDT' : 'USDT'}</span>
            </div>
            <div className="enhanced-cost-calculator__breakdown-item">
              <span>Exchange Rate:</span>
              <span>1 USDT = {calculation.exchangeRate.toFixed(4)} HKD</span>
            </div>
            <div className="enhanced-cost-calculator__breakdown-item">
              <span>FX Cost:</span>
              <span>{calculation.fxCost.toFixed(2)} HKD</span>
            </div>
            <div className="enhanced-cost-calculator__breakdown-item">
              <span>Processing Fee:</span>
              <span>{calculation.processingFee.toFixed(2)} HKD</span>
            </div>
            <div className="enhanced-cost-calculator__breakdown-item enhanced-cost-calculator__breakdown-item--total">
              <span>Total StablePay Cost:</span>
              <span>{calculation.totalCost.toFixed(2)} HKD</span>
            </div>
          </div>

          {/* Simple Chart Visualization */}
          <div className="enhanced-cost-calculator__chart">
            {chartType === 'bar' && costBreakdownData && (
              <BarChart data={costBreakdownData} />
            )}
            {chartType === 'pie' && costBreakdownData && (
              <PieChartComponent data={costBreakdownData} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Annual Projection Results Component
interface AnnualProjectionResultsProps {
  calculation: CostCalculation
  projection: AnnualProjection
  mode?: 'business' | 'customer'
}

const AnnualProjectionResults: React.FC<AnnualProjectionResultsProps> = ({
  calculation,
  projection,
  mode = 'business'
}) => {
  return (
    <div className="enhanced-cost-calculator__annual">
      <div className="enhanced-cost-calculator__annual-header">
        <h3>{mode === 'customer' ? 'Your Annual Savings' : 'Annual Savings Projection'}</h3>
        <p>
          {mode === 'customer' 
            ? `If you send ${projection.monthlyVolume.toLocaleString()} HKD monthly to Hong Kong`
            : `Based on ${projection.monthlyVolume.toLocaleString()} HKD monthly volume`
          }
        </p>
      </div>

      <div className="enhanced-cost-calculator__annual-grid">
        <div className="enhanced-cost-calculator__annual-card">
          <div className="enhanced-cost-calculator__annual-card-header">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <h4>{mode === 'customer' ? 'Money You Save' : 'Annual Savings'}</h4>
          </div>
          <div className="enhanced-cost-calculator__annual-value enhanced-cost-calculator__annual-value--savings">
            {projection.annualSavings.toLocaleString()} HKD
          </div>
          <p className="enhanced-cost-calculator__annual-description">
            {mode === 'customer' 
              ? 'Extra money in your pocket every year'
              : 'Total savings compared to traditional methods'
            }
          </p>
        </div>

        <div className="enhanced-cost-calculator__annual-card">
          <div className="enhanced-cost-calculator__annual-card-header">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <h4>{mode === 'customer' ? 'Annual Transfers' : 'Annual Volume'}</h4>
          </div>
          <div className="enhanced-cost-calculator__annual-value">
            {projection.annualVolume.toLocaleString()} HKD
          </div>
          <p className="enhanced-cost-calculator__annual-description">
            {mode === 'customer' 
              ? 'Total amount you send per year'
              : 'Total payment volume per year'
            }
          </p>
        </div>

        <div className="enhanced-cost-calculator__annual-card">
          <div className="enhanced-cost-calculator__annual-card-header">
            <Clock className="w-5 h-5 text-purple-600" />
            <h4>{mode === 'customer' ? 'Time Saved' : 'Break-even'}</h4>
          </div>
          <div className="enhanced-cost-calculator__annual-value">
            {mode === 'customer' ? '99%' : `${projection.breakEvenMonths} months`}
          </div>
          <p className="enhanced-cost-calculator__annual-description">
            {mode === 'customer' 
              ? 'Faster than traditional transfers'
              : 'Time to recover setup costs'
            }
          </p>
        </div>

        <div className="enhanced-cost-calculator__annual-card">
          <div className="enhanced-cost-calculator__annual-card-header">
            <Shield className="w-5 h-5 text-orange-600" />
            <h4>{mode === 'customer' ? 'Cost Savings' : 'Cost Reduction'}</h4>
          </div>
          <div className="enhanced-cost-calculator__annual-value">
            {calculation.savingsPercentage.toFixed(1)}%
          </div>
          <p className="enhanced-cost-calculator__annual-description">
            {mode === 'customer' 
              ? 'Less than traditional transfers'
              : 'Average cost reduction per transaction'
            }
          </p>
        </div>
      </div>

      {/* Annual Comparison Chart */}
      <div className="enhanced-cost-calculator__annual-comparison">
        <h4>{mode === 'customer' ? 'Your Annual Cost Comparison' : 'Annual Cost Comparison'}</h4>
        <div className="enhanced-cost-calculator__annual-bars">
          <div className="enhanced-cost-calculator__annual-bar">
            <div className="enhanced-cost-calculator__annual-bar-label">
              {mode === 'customer' ? 'Traditional Bank Transfers' : 'Traditional'}
            </div>
            <div className="enhanced-cost-calculator__annual-bar-container">
              <div 
                className="enhanced-cost-calculator__annual-bar-fill enhanced-cost-calculator__annual-bar-fill--traditional"
                style={{ width: '100%' }}
              ></div>
              <span className="enhanced-cost-calculator__annual-bar-value">
                {projection.traditionalAnnualCost.toLocaleString()} HKD
              </span>
            </div>
          </div>
          <div className="enhanced-cost-calculator__annual-bar">
            <div className="enhanced-cost-calculator__annual-bar-label">
              {mode === 'customer' ? 'Stablecoin Payments' : 'StablePay'}
            </div>
            <div className="enhanced-cost-calculator__annual-bar-container">
              <div 
                className="enhanced-cost-calculator__annual-bar-fill enhanced-cost-calculator__annual-bar-fill--stablepay"
                style={{ width: `${(projection.stablePayAnnualCost / projection.traditionalAnnualCost) * 100}%` }}
              ></div>
              <span className="enhanced-cost-calculator__annual-bar-value">
                {projection.stablePayAnnualCost.toLocaleString()} HKD
              </span>
            </div>
          </div>
        </div>
        {mode === 'customer' && (
          <div className="enhanced-cost-calculator__annual-highlight">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span>
              You could save {projection.annualSavings.toLocaleString()} HKD per year by switching to stablecoin payments
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// Simple Bar Chart Component
interface BarChartProps {
  data: Array<{ name: string; value: number; color: string }>
}

const BarChart: React.FC<BarChartProps> = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.value))

  return (
    <div className="enhanced-cost-calculator__bar-chart">
      {data.map((item, index) => (
        <div key={index} className="enhanced-cost-calculator__bar-item">
          <div className="enhanced-cost-calculator__bar-label">{item.name}</div>
          <div className="enhanced-cost-calculator__bar-container">
            <div 
              className="enhanced-cost-calculator__bar-fill"
              style={{ 
                width: `${(item.value / maxValue) * 100}%`,
                backgroundColor: item.color
              }}
            ></div>
            <span className="enhanced-cost-calculator__bar-value">
              {item.value.toFixed(2)} HKD
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

// Simple Pie Chart Component
interface PieChartComponentProps {
  data: Array<{ name: string; value: number; color: string }>
}

const PieChartComponent: React.FC<PieChartComponentProps> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <div className="enhanced-cost-calculator__pie-chart">
      <div className="enhanced-cost-calculator__pie-legend">
        {data.map((item, index) => (
          <div key={index} className="enhanced-cost-calculator__pie-legend-item">
            <div 
              className="enhanced-cost-calculator__pie-legend-color"
              style={{ backgroundColor: item.color }}
            ></div>
            <span className="enhanced-cost-calculator__pie-legend-label">
              {item.name}: {item.value.toFixed(2)} HKD ({((item.value / total) * 100).toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default EnhancedCostCalculator